import React, { useState, useEffect, useRef } from 'react';
import { Box, Text, Button, HStack } from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';
import { usePomodoroSettings } from '../PomodoroSettingsContext';
import BannerArea from './BannerArea';

const PomodoroTimer = () => {
  const { workDuration, breakDuration, timerSound } = usePomodoroSettings();

  const [isActive, setIsActive] = useState(false);
  const [isBreak, setIsBreak] = useState(false);
  const [remaining, setRemaining] = useState(workDuration);
  const [targetTime, setTargetTime] = useState<number | null>(null);
  const [preAlarmPlayed, setPreAlarmPlayed] = useState(false);

  const audioMap = useRef<{ [key: string]: HTMLAudioElement }>({});
  const [audioPrepared, setAudioPrepared] = useState(false);

  const getFileName = (name: string) => {
    switch (name) {
      case 'School Bell':
        return '/sounds/Japanese_School_Bell04-1(Long).mp3';
      case 'Church Bell':
        return '/sounds/Church_Bell03-09(Low-Long).mp3';
      case 'Temple Bell':
        return '/sounds/Bonsho03-1(High).mp3';
      case 'start':
        return '/sounds/Time_Signal-Beep03-1(Long).mp3';
      default:
        return '/sounds/Japanese_School_Bell04-1(Long).mp3';
    }
  };

  const prepareSounds = () => {
    if (audioPrepared) return;

    ['School Bell', 'Church Bell', 'Temple Bell', 'start'].forEach((name) => {
      const audio = new Audio(getFileName(name));
      audio.load();
      audio.play().catch((err) => console.warn(err));
      audio.pause();
      audio.currentTime = 0;
      audioMap.current[name] = audio;
    });
    setAudioPrepared(true);
  };

  const playSound = (name: string) => {
    const audio = audioMap.current[name];
    if (audio) {
      audio.currentTime = 0;
      audio.play().catch((err) => console.warn(err));
    } else {
      const newAudio = new Audio(getFileName(name));
      newAudio.play().catch((err) => console.warn(err));
    }
  };

  useEffect(() => {
    if (!isActive) return;

    const update = () => {
      if (targetTime == null) return;
      const now = Date.now();
      const diff = Math.max(0, Math.floor((targetTime - now) / 1000));
      setRemaining(diff);

      if (isBreak && diff === 3 && !preAlarmPlayed) {
        playSound('start');
        setPreAlarmPlayed(true);
      }

      if (diff <= 0) {
        if (!isBreak) {
          playSound(timerSound);
          setIsBreak(true);
          setTargetTime(Date.now() + breakDuration * 1000);
          setRemaining(breakDuration);
          setPreAlarmPlayed(false);
        } else {
          setIsBreak(false);
          setTargetTime(Date.now() + workDuration * 1000);
          setRemaining(workDuration);
        }
      }
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [isActive, targetTime, isBreak]);

  // ⬇️ document.title をカウントダウンに反映
  useEffect(() => {
    if (!isActive) {
      document.title = 'ポモドーロタイマー';
      return;
    }

    const minutes = Math.floor(remaining / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (remaining % 60).toString().padStart(2, '0');
    document.title = `${minutes}:${seconds} - ${isBreak ? '休憩中' : '作業中'}`;

    return () => {
      document.title = 'ポモドーロタイマー';
    };
  }, [remaining, isActive, isBreak]);

  const handleStartStop = () => {
    prepareSounds();
    if (isActive) {
      setIsActive(false);
    } else {
      const now = Date.now();
      setTargetTime(now + remaining * 1000);
      setIsActive(true);
    }
  };

  const handleReset = () => {
    setIsActive(false);
    setIsBreak(false);
    setRemaining(workDuration);
    setTargetTime(null);
  };

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60)
      .toString()
      .padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="center"
      height="100%"
      className="PomodoroTimer"
      pt={20}
    >
      <Box
        position="relative"
        w="300px"
        h="300px"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <svg width="300" height="300">
          <circle
            cx="150"
            cy="150"
            r="130"
            stroke="rgba(255, 255, 255, 0.2)"
            strokeWidth="12"
            fill="none"
          />
          <circle
            cx="150"
            cy="150"
            r="130"
            stroke="white"
            strokeWidth="12"
            fill="none"
            strokeDasharray={2 * Math.PI * 130}
            strokeDashoffset={(
              2 *
              Math.PI *
              130 *
              (1 - remaining / (isBreak ? breakDuration : workDuration))
            ).toFixed(2)}
            strokeLinecap="round"
            transform="rotate(-90 150 150)"
          />
        </svg>

        <Box
          position="absolute"
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Text fontSize="6xl" fontWeight="bold">
            {formatTime(remaining)}
          </Text>
          <Text mt={2} fontSize="md">
            {isBreak ? 'Break Time' : isActive ? 'Working' : 'Start Working'}
          </Text>
        </Box>
      </Box>

      <HStack mt={8} spacing={4}>
        <Button
          onClick={handleStartStop}
          borderRadius="full"
          w="60px"
          h="60px"
          colorScheme="white"
          variant="outline"
          fontSize="2xl"
        >
          {isActive ? (
            <HStack spacing="6px">
              <Box w="4px" h="20px" bg="currentColor" borderRadius="sm" />
              <Box w="4px" h="20px" bg="currentColor" borderRadius="sm" />
            </HStack>
          ) : (
            '▶'
          )}
        </Button>
        <Button
          onClick={handleReset}
          borderRadius="full"
          w="60px"
          h="60px"
          colorScheme="white"
          variant="outline"
          fontSize="2xl"
        >
          <RepeatIcon boxSize={5} />
        </Button>
      </HStack>
      <BannerArea />
    </Box>
  );
};

export default PomodoroTimer;
